/**
 * Seleção leve de trechos relevantes (sem embeddings/API) para reduzir contexto enviado à IA.
 */

const STOPWORDS_PT = new Set(
  (
    'a o e de da do das dos em no na nos nas um uma uns umas para por com que se ao aos as os ' +
    'nao sim ser sao sua seu suas seus este esta estes estas isso isto aquele aquela qual quais ' +
    'quando onde como porque pra pelo pela pelos pelas mais menos muito pouco entre sobre sob ate ' +
    'tem ter foi era ja la ele ela eles elas meu minha seus dele dela'
  ).split(/\s+/)
);

function normalizar(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function termosConsulta(consulta) {
  return [
    ...new Set(
      normalizar(consulta)
        .split(' ')
        .filter((t) => t.length >= 3 && !STOPWORDS_PT.has(t))
    ),
  ];
}

/** Agrupa parágrafos em blocos de ~tamanho caracteres, preservando a ordem. */
export function dividirEmChunks(texto, tamanho) {
  const paras = texto.split(/\n{2,}/);
  const chunks = [];
  let buf = '';
  for (const p of paras) {
    const candidato = buf ? `${buf}\n\n${p}` : p;
    if (candidato.length > tamanho && buf) {
      chunks.push(buf);
      buf = p;
    } else {
      buf = candidato;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

/**
 * BM25-like scoring over an array of chunk strings (k1=1.2, b=0.75).
 * @param {string[]} chunks
 * @param {string} consulta
 * @returns {Array<{ i: number, score: number, c: string }>}
 */
export function scoreBm25Chunks(chunks, consulta) {
  const termos = termosConsulta(consulta);
  if (!chunks?.length || !termos.length) {
    return (chunks || []).map((c, i) => ({ i, score: 0, c }));
  }
  const norms = chunks.map((c) => normalizar(c));
  const avgdl = norms.reduce((s, c) => s + c.split(' ').length, 0) / Math.max(1, norms.length);
  const df = {};
  for (const t of termos) {
    df[t] = norms.reduce((n, c) => n + (c.includes(t) ? 1 : 0), 0);
  }
  const N = norms.length;
  const k1 = 1.2;
  const b = 0.75;
  return chunks.map((c, i) => {
    const cn = norms[i];
    const dl = cn.split(' ').filter(Boolean).length || 1;
    let score = 0;
    for (const t of termos) {
      if (!cn.includes(t)) continue;
      const tf = cn.split(t).length - 1;
      const idf = Math.log(1 + (N - (df[t] || 0) + 0.5) / ((df[t] || 0) + 0.5));
      const denom = tf + k1 * (1 - b + b * (dl / Math.max(1, avgdl)));
      score += idf * ((tf * (k1 + 1)) / Math.max(0.001, denom));
    }
    return { i, score, c };
  });
}

/**
 * Retorna trechos do texto mais relevantes para a consulta, respeitando orçamento de caracteres.
 * Usa BM25 leve (sem embeddings).
 * @param {string} texto
 * @param {string} consulta
 * @param {{ budget?: number, tamanhoChunk?: number }} [opts]
 * @returns {string}
 */
export function selecionarTrechosRelevantes(texto, consulta, opts = {}) {
  const budget = opts.budget ?? 60000;
  const tamanhoChunk = opts.tamanhoChunk ?? 1200;

  if (!texto) return '';
  if (texto.length <= budget) return texto;

  const termos = termosConsulta(consulta);
  if (termos.length === 0) return texto.slice(0, budget);

  const chunks = dividirEmChunks(texto, tamanhoChunk);
  const scored = scoreBm25Chunks(chunks, consulta);
  const comScore = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
  if (comScore.length === 0) return texto.slice(0, budget);

  const selecionados = [];
  let total = 0;
  for (const s of comScore) {
    if (total + s.c.length > budget) continue;
    selecionados.push(s);
    total += s.c.length;
    if (total >= budget) break;
  }
  if (selecionados.length === 0) return texto.slice(0, budget);

  selecionados.sort((a, b) => a.i - b.i);
  return selecionados.map((s) => s.c).join('\n\n[...]\n\n');
}
